import { useEffect, useState, useCallback } from "react";
import { Archive, Download, Trash2, MapPin, Cpu, Send, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmt } from "@/lib/api";
import ConfirmDialog from "@/components/common/ConfirmDialog";

const PKG_STATUS_LABEL = { pending: "Requested — pending", accepted: "Accepted", rejected: "Rejected", making: "Making", delivery: "Out for delivery", delivered: "Delivered" };

export default function History() {
  const [entries, setEntries] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [pkgRequests, setPkgRequests] = useState([]);
  const [pkgBusy, setPkgBusy] = useState(false);

  const load = useCallback(() => api.get("/history").then((r) => setEntries(r.data)).catch((e) => toast.error(errMsg(e))), []);
  const loadPkgRequests = useCallback(() => api.get("/package-requests").then((r) => setPkgRequests(r.data)).catch(() => {}), []);
  useEffect(() => { load(); loadPkgRequests(); }, [load, loadPkgRequests]);

  const current = entries.find((e) => e.id === selectedId) || entries[0] || null;
  useEffect(() => { setSendOpen(false); }, [current?.id]);
  const pkgRequest = current ? pkgRequests.find((p) => p.history_id === current.id) : null;

  const requestPackage = async () => {
    if (!current) return;
    setPkgBusy(true);
    try {
      await api.post(`/history/${current.id}/request-package`);
      toast.success("Package requested", { description: "Sent to the super admin for approval." });
      loadPkgRequests();
    } catch (e) { toast.error(errMsg(e)); } finally { setPkgBusy(false); }
  };

  const download = async (entry) => {
    setBusy(true);
    try {
      const res = await api.get(`/history/${entry.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `KyraLink_${entry.serial}_history_report.pdf` });
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const openSend = () => { setSendEmail(current.client_email || ""); setSendOpen(true); };

  const sendProposal = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/history/${current.id}/send`, { to_email: sendEmail });
      toast.success("Proposal sent", { description: sendEmail });
      setSendOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSending(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/history/${removing.id}`);
      toast.success("History entry deleted");
      setRemoving(null);
      if (selectedId === removing.id) setSelectedId(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const s = current?.sizing;
  const u = current?.usage;

  return (
    <div className="h-full grid grid-cols-[380px_1fr]" data-testid="history-page">
      <aside className="border-r border-kl-line flex flex-col bg-kl-black">
        <div className="p-6 border-b border-kl-line">
          <p className="kl-label-dark">Archive</p>
          <h1 className="font-display font-light text-2xl mt-1">History <span className="font-mono text-sm text-kl-muted">{entries.length}</span></h1>
          <p className="text-xs text-kl-muted mt-2 leading-relaxed">Confirmed proposals — the client, readings and sizing data for a meter's past engagement, kept after the device is freed for a new client.</p>
        </div>
        <ul className="flex-1 overflow-y-auto kl-scroll">
          {entries.length === 0 && <li className="p-6 text-sm text-kl-muted" data-testid="history-empty">Nothing archived yet. Confirm a proposal from its Reports page to move it here.</li>}
          {entries.map((e) => (
            <li key={e.id} className={`border-b border-kl-line ${current?.id === e.id ? "bg-kl-surface" : "hover:bg-kl-surface"} transition-colors duration-150`} data-testid={`history-row-${e.id}`}>
              <button onClick={() => setSelectedId(e.id)} className="w-full text-left p-4 kl-focus">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display text-sm truncate">{e.client_name}</span>
                  <span className="kl-label-dark">{e.client_type}</span>
                </div>
                <div className="font-mono text-[11px] text-kl-muted mt-1 truncate">{e.serial}</div>
                <div className="text-[11px] text-kl-muted mt-1">{e.city || e.country ? `${e.city}${e.city && e.country ? ", " : ""}${e.country} · ` : ""}confirmed {fmt.date(e.confirmed_at)}</div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="bg-white text-kl-black overflow-y-auto kl-scroll-light">
        {current ? (
          <div className="max-w-4xl mx-auto p-8 animate-rise" data-testid="history-detail">
            <div className="flex items-start justify-between gap-6 pb-6 border-b border-kl-lineLight">
              <div className="min-w-0">
                <p className="kl-label">{current.serial} · {current.client_type} · confirmed {fmt.time(current.confirmed_at)} by {current.confirmed_by}</p>
                <h2 className="font-display font-light text-3xl mt-1 truncate">{current.client_name}</h2>
                {(current.address || current.city || current.country) && (
                  <p className="text-sm text-kl-mutedLight mt-1 flex items-start gap-1.5"><MapPin size={13} className="shrink-0 mt-0.5" /> {[current.address, current.city, current.country].filter(Boolean).join(", ")}</p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => download(current)} disabled={busy} data-testid="history-download-button" className="kl-btn-dark"><Download size={15} /> Download PDF</button>
                <button onClick={() => setRemoving(current)} data-testid="history-delete-button" title="Delete" className="kl-btn-outline px-3 hover:bg-kl-inactive hover:border-kl-inactive"><Trash2 size={15} /></button>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-6 mt-6">
              <div className="col-span-12 lg:col-span-7 space-y-4">
                <div className="kl-panel p-5">
                  <p className="kl-label">Measured consumption</p>
                  <div className="grid grid-cols-3 gap-px bg-kl-lineLight border border-kl-lineLight mt-3">
                    {[["Avg daily", `${u?.avg_daily_kwh ?? 0} kWh`], ["Peak load", `${u?.peak_kw ?? 0} kW`], ["Total", `${u?.total_kwh ?? 0} kWh`]].map(([l, v]) => (
                      <div key={l} className="bg-white p-3"><div className="kl-label">{l}</div><div className="font-mono text-lg mt-1">{v}</div></div>
                    ))}
                  </div>
                  <p className="text-xs text-kl-mutedLight mt-2">{u?.days ?? 0} days monitored · {u?.readings_count ?? 0} samples</p>
                </div>
                <div className="kl-panel p-5">
                  <p className="kl-label">{current.report_title}</p>
                  <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap text-kl-black">{current.report_body}</p>
                </div>
              </div>
              <div className="col-span-12 lg:col-span-5 space-y-4">
                <div className="kl-panel-dark p-5">
                  <p className="kl-label-dark flex items-center gap-1.5"><Cpu size={12} /> Recommended system</p>
                  {s && (
                    <div className="mt-3 space-y-2 font-mono text-sm">
                      <div className="flex justify-between"><span className="text-kl-muted">Panels</span><span>{s.pv?.count} × {s.pv?.panel?.rating} W</span></div>
                      <div className="flex justify-between"><span className="text-kl-muted">Array</span><span>{s.pv?.array_kw} kWp</span></div>
                      <div className="flex justify-between"><span className="text-kl-muted">Inverter</span><span>{s.inverter?.count} × {s.inverter?.recommended?.rating} kW</span></div>
                      <div className="flex justify-between"><span className="text-kl-muted">Battery</span><span>{s.battery?.total_kwh} kWh</span></div>
                      <div className="flex justify-between border-t border-kl-line pt-2 mt-2"><span className="text-kl-muted">Cost</span><span className="text-kl-green">{fmt.money(s.economics?.system_cost, s.economics?.currency)}</span></div>
                      <div className="flex justify-between"><span className="text-kl-muted">Payback</span><span>{s.economics?.payback_years} yrs</span></div>
                    </div>
                  )}
                </div>
                <div className="kl-panel p-5">
                  <p className="kl-label flex items-center gap-1.5"><Archive size={12} /> Delivery log</p>
                  {current.sent_log?.length ? (
                    <ul className="mt-3 space-y-2">
                      {current.sent_log.slice().reverse().map((e, i) => (
                        <li key={i} className="text-xs"><span className="font-mono">{e.to}</span><br /><span className="text-kl-mutedLight">{fmt.time(e.at)} · by {e.by} · {e.channel}</span></li>
                      ))}
                    </ul>
                  ) : <p className="text-xs text-kl-mutedLight mt-2">This proposal was never sent before being confirmed.</p>}
                </div>

                {current.report_status !== "sent" && (
                  sendOpen ? (
                    <form onSubmit={sendProposal} className="kl-panel p-5" data-testid="history-send-form">
                      <p className="kl-label">Send proposal</p>
                      <input
                        type="email" required autoFocus value={sendEmail} onChange={(e) => setSendEmail(e.target.value)}
                        placeholder="client@example.com" className="kl-input mt-2" data-testid="history-send-email-input"
                      />
                      <div className="flex justify-end gap-2 mt-4">
                        <button type="button" onClick={() => setSendOpen(false)} className="kl-btn-outline h-9 px-3 text-xs">Cancel</button>
                        <button disabled={sending} className="kl-btn-primary h-9 px-3 text-xs" data-testid="history-send-submit"><Send size={13} /> {sending ? "Sending…" : "Send"}</button>
                      </div>
                    </form>
                  ) : (
                    <button onClick={openSend} className="kl-btn-outline w-full h-11" data-testid="history-send-button"><Send size={15} /> Send proposal</button>
                  )
                )}

                {pkgRequest ? (
                  <div className="kl-panel p-4 flex items-center gap-2" data-testid="history-package-status">
                    <PackageCheck size={15} className="text-kl-green shrink-0" />
                    <span className="text-sm">Package: <span className="font-display">{PKG_STATUS_LABEL[pkgRequest.status] || pkgRequest.status}</span></span>
                  </div>
                ) : (
                  <button onClick={requestPackage} disabled={pkgBusy} className="kl-btn-outline w-full h-11" data-testid="history-request-package-button">
                    <PackageCheck size={15} /> {pkgBusy ? "Requesting…" : "Request package"}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center p-10" data-testid="history-empty-state">
            <Archive size={28} className="text-kl-mutedLight" />
            <h2 className="font-display text-2xl mt-4">No archived engagements</h2>
            <p className="text-sm text-kl-mutedLight mt-1 max-w-sm">Confirming a proposal moves its client, readings and sizing data here, and frees the meter for a new client.</p>
          </div>
        )}
      </section>

      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} title="Delete this history entry?" description={removing ? `${removing.client_name} — ${removing.serial}. This cannot be undone.` : ""} confirmLabel="Delete" onConfirm={remove} />
    </div>
  );
}
